import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CoContributorNoteDto from "../../../types/feedback/CoContributorNoteDto";
import { FormattedDate } from "../../common";

interface FeedbackReplyPreviewProps {
  notes: CoContributorNoteDto[];
  isExpanded: boolean;
  onToggle: () => void;
}

function FeedbackReplyPreview({
  notes,
  isExpanded,
  onToggle,
}: FeedbackReplyPreviewProps) {
  const { t } = useTranslation();
  const previewRef = useRef<HTMLParagraphElement>(null);
  const [contentExceedsPreview, setContentExceedsPreview] = useState(false);

  const previewBody = notes[0]?.body ?? "";

  useEffect(() => {
    if (notes.length > 1) {
      setContentExceedsPreview(true);
      return;
    }

    if (isExpanded) {
      return;
    }

    const el = previewRef.current;
    if (!el) {
      return;
    }

    const measureOverflow = () => {
      setContentExceedsPreview(el.scrollHeight > el.clientHeight + 1);
    };

    measureOverflow();

    const observer = new ResizeObserver(measureOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [previewBody, isExpanded, notes.length]);

  if (notes.length === 0) {
    return null;
  }

  const showToggle = notes.length > 1 || contentExceedsPreview;
  const notesToRender = isExpanded ? notes : [notes[0]];

  return (
    <div className="mt-1.5 border-l-2 border-l-gray-200 pl-2.5">
      {notesToRender.map((note, index) => (
        <div
          key={note.id}
          className={notesToRender.length > 1 ? "mt-2 first:mt-0" : undefined}
        >
          {isExpanded && notes.length > 1 && (
            <span className="text-xs text-gray-400 block mb-0.5">
              <FormattedDate
                date={new Date(note.dateCreated)}
                displayMode="FROM-NOW"
                convertFromUtc
              />
            </span>
          )}
          <p
            ref={!isExpanded && index === 0 ? previewRef : undefined}
            className={`text-sm text-gray-600 whitespace-pre-wrap break-words leading-relaxed ${
              !isExpanded ? "line-clamp-2" : ""
            }`}
          >
            {note.body}
          </p>
        </div>
      ))}
      {showToggle && (
        <button
          type="button"
          className="text-xs text-primary hover:underline mt-1"
          onClick={onToggle}
        >
          {isExpanded
            ? t("Feedback.CollabDoc.ShowLess", { defaultValue: "Show less" })
            : t("Feedback.CollabDoc.ViewMore", { defaultValue: "View more" })}
        </button>
      )}
    </div>
  );
}

export default FeedbackReplyPreview;
